import { API_URL } from "../config/api";

// Registration interfaces
export interface RegisterUserData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  username: string;
}

export interface RegisterUserResponse {
  id: string;
}

export interface RegistrationError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

export interface UserInterface {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  updated_at: string | null;
  active_to: string;
}

// Register new user function
export const registerUser = async (userData: RegisterUserData): Promise<RegisterUserResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    });

    // Handle successful registration
    if (response.status === 201) {
      const data: RegisterUserResponse = await response.json();
      return data;
    }

    // Handle specific error cases
    let errorMessage = 'Registration failed';
    let errorData: any = {};
    
    try {
      errorData = await response.json();
    } catch (e) {
      // If response body is not JSON
      errorData = { message: 'Server error occurred' };
    }

    const error: RegistrationError = {
      status: response.status,
      message: errorMessage,
      errors: errorData.errors
    };

    switch (response.status) {
      case 400:
        error.message = 'Server error. Please try again later.';
        break;
      case 403:
        error.message = errorData.detail || errorData.message || 'Registration is currently disabled.';
        break;
      case 409:
        error.message = errorData.detail || errorData.message || 'A user with this email already exists.';
        break;
      case 422:
        error.message = errorData.detail || errorData.message || 'Please check your input and try again.';
        break;
      default:
        error.message = errorData.detail || errorData.message || 'An unexpected error occurred. Please try again.';
    }

    // Throw error with status for better handling in the UI
    const errorWithStatus = new Error(error.message);
    (errorWithStatus as any).status = response.status;
    (errorWithStatus as any).errors = error.errors;
    throw errorWithStatus;
    
  } catch (error: any) {
    // Re-throw if it's already our custom error
    if (error.status) {
      throw error;
    }
    // Handle network errors
    throw new Error('Network error. Please check your connection and try again.');
  }
};

export const getUsers = async (token: string): Promise<ProjectUsersInterface> => {
  try {
    const r = await fetch(`${API_URL}/users/?size=1000&t=${new Date().getTime()}`, {
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectUsersInterface = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create new user
 * @param {string} token - The authorization token
 * @param {any} userData - User data to create
 * @returns {Promise<any>} Created user data
 */
export const createUser = async (token: string, userData: any): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/users/`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(userData)
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user');
  }
};

/**
 * Get user profile
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @returns {Promise<UserProfile>} User profile data
 */
export const getUserProfile = async (token: string, userId: string): Promise<UserProfile> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/profile`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserProfile = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get user profile');
  }
};

/**
 * Create user profile (for new users)
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {UserProfileFormData} profileData - Profile data to create
 * @returns {Promise<UserProfile>} Created user profile
 */
export const createUserProfile = async (
  token: string, 
  userId: string, 
  profileData: UserProfileFormData
): Promise<UserProfile> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/profile`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        id: userId,
        ...profileData
      })
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserProfile = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user profile');
  }
};

/**
 * Update user profile
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {UserProfileFormData} profileData - Profile data to update
 * @returns {Promise<UserProfile>} Updated user profile
 */
export const updateUserProfile = async (
  token: string, 
  userId: string, 
  profileData: UserProfileFormData
): Promise<UserProfile> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        id: userId,
        ...profileData
      })
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserProfile = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update user profile');
  }
};

/**
 * Save user profile (create or update automatically)
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {UserProfileFormData} profileData - Profile data to save
 * @param {boolean} isNewProfile - Whether this is a new profile
 * @returns {Promise<UserProfile>} Saved user profile
 */
export const saveUserProfile = async (
  token: string, 
  userId: string, 
  profileData: UserProfileFormData,
  isNewProfile: boolean = false
): Promise<UserProfile> => {
  try {
    // Try update first, fall back to create if it fails
    if (!isNewProfile) {
      try {
        return await updateUserProfile(token, userId, profileData);
      } catch (error) {
        // If update fails with 404 or 422, try create
        if (error instanceof Error && (error.message.includes('404') || error.message.includes('422'))) {
          console.log('🔄 Profile update failed, trying create...');
          return await createUserProfile(token, userId, profileData);
        }
        throw error;
      }
    } else {
      return await createUserProfile(token, userId, profileData);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to save user profile');
  }
};

/**
 * Get user accounts
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @returns {Promise<UserAccount[]>} User accounts
 */
export const getUserAccounts = async (token: string, userId: string): Promise<UserAccount[]> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount[] = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get user accounts');
  }
};

/**
 * Create user account
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {UserAccountFormData} accountData - Account data
 * @returns {Promise<UserAccount>} Created account
 */
export const createUserAccount = async (
  token: string, 
  userId: string, 
  accountData: UserAccountFormData
): Promise<UserAccount> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(accountData)
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user account');
  }
};

/**
 * Get account details
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {string} accountId - Account ID
 * @returns {Promise<UserAccount>} Account details
 */
export const getAccountDetails = async (
  token: string, 
  userId: string, 
  accountId: string
): Promise<UserAccount> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts/${accountId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get account details');
  }
};

/**
 * Add team member to account
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {string} accountId - Account ID
 * @param {string} memberId - Member ID to add
 * @returns {Promise<void>} Success
 */
export const addTeamMember = async (
  token: string, 
  userId: string, 
  accountId: string, 
  memberId: string
): Promise<void> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts/${accountId}/users/${memberId}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to add team member');
  }
};

/**
 * Remove team member from account
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {string} accountId - Account ID
 * @param {string} memberId - Member ID to remove
 * @returns {Promise<void>} Success
 */
export const removeTeamMember = async (
  token: string, 
  userId: string, 
  accountId: string, 
  memberId: string
): Promise<void> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts/${accountId}/users/${memberId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to remove team member');
  }
};

/**
 * Get available account roles
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @returns {Promise<UserRole[]>} Available roles
 */
export const getAvailableRoles = async (token: string, userId: string): Promise<UserRole[]> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/accounts/roles`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserRole[] = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get available roles');
  }
};

/**
 * Upload user document
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {string} documentType - Document type (nda, contract, driver_license)
 * @param {File} file - File to upload
 * @returns {Promise<{url: string}>} Upload result
 */
export const uploadUserDocument = async (
  token: string,
  userId: string,
  documentType: string,
  file: File
): Promise<{url: string}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/users/${userId}/profile/documents/${documentType}`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to upload user document');
  }
};

/**
 * Delete user document
 * @param {string} token - The authorization token
 * @param {string} userId - User ID
 * @param {string} documentType - Document type (nda, contract, driver_license)
 * @returns {Promise<void>} Success
 */
export const deleteUserDocument = async (
  token: string,
  userId: string,
  documentType: string
): Promise<void> => {
  try {
    const r = await fetch(`${API_URL}/users/${userId}/profile/documents/${documentType}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to delete user document');
  }
};

// Forgot Password interfaces
export interface ForgotPasswordData {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

/**
 * Send forgot password email
 * @param {ForgotPasswordData} emailData - Email data for password reset
 * @returns {Promise<ForgotPasswordResponse>} Response with success message
 */
export const forgotPassword = async (emailData: ForgotPasswordData): Promise<ForgotPasswordResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send password reset email');
    }

    const data: ForgotPasswordResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to send password reset email');
  }
};

// Resend Invitation interfaces
export interface ResendInvitationData {
  email: string;
}

export interface ResendInvitationResponse {
  message: string;
}

/**
 * Resend invitation email to user
 * @param {string} token - The authorization token
 * @param {ResendInvitationData} emailData - Email data
 * @returns {Promise<ResendInvitationResponse>} Response message
 */
export const resendInvitation = async (
  token: string,
  emailData: ResendInvitationData
): Promise<ResendInvitationResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/resend-confirmation-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(emailData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to resend invitation');
    }

    const data: ResendInvitationResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to resend invitation');
  }
};